import { observer } from 'mobx-react';
import React, { useEffect, useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  StandardDialog,
  createAutocompleteAddressField,
  createAutocompleteField,
  createCheckboxField,
  createPhoneFieldClick,
  createSelectField,
  createTextField,
} from '@roc/ui';
import {
  Box,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  InputAdornment,
  Tooltip,
  Tab,
  Tabs as MuiTabs,
  Chip,
  TextField,
  lighten
} from '@material-ui/core';
import { TestDataAttribute, Card, Tabs } from '@roc/ui';
import { formatDate, sanitizeTestId } from '@roc/client-portal-shared/utils';
import { useUserStore } from '@roc/feature-app-core';
import {
  TPO_OBJECT_TYPE,
  dataSourceGroupOptions,
  leadExperienceOptions,
  leadProfessionOptions,
  leadTypeOptions,
  leadStatusOptions as statusOptions,
  leadSubStatusOptions,
  leadNewSubStatusOptions,
  leadAttemptingToContactSubStatusOptions,
  leadFitSubStatusOptions,
  leadNoFitSubStatusOptions,
} from '../leads/constants/constants';
import { SendBulkEmailsModal } from '../leadsDashboard/sendBulkEmails';
import { SendBulkMessagesModal } from 'apps/crm-portal/src/app/modules/ringCentral/sendBulkMessages';
import { recordTypeLeadOptions } from '../../stores/editLeadsDashboardStore';
import { ContactActivitiesCard } from '../notesAndActivities/contactActivitiesCard';
import { ContactNotesCard } from '../notesAndActivities/contactNotesCard';
import { LEAD_OBJECT_TYPE } from '../leads/constants/constants';
import { ContactTasksCard } from '../notesAndActivities/contactTasksCard';
import { ContactHistoryCard } from '../notesAndActivities/contactHistoryCard';
import { ConvertLeadToLenderModal } from '../leadsDashboard/convertLeadToLenderModal';
import { AdditionalContactsCard } from '../notesAndActivities/additionalContactsCard';
import { HelpOutline } from '@material-ui/icons';
import { ReferrerCardComponent } from '@roc/feature-referrals';
import { RealEstateTransactions } from '../notesAndActivities/realEstateTransactions';
import SalesUserTag from './salesUserTag';
import SmartphoneIcon from '@material-ui/icons/Smartphone';
import PhoneIcon from '@material-ui/icons/Phone';
import { useNewLeadStore } from '../../hooks/useNewLeadStore';

const useStyles = makeStyles(theme => ({
  button: {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(2),
    minWidth: '100px',
  },
  noteTime: {
    display: 'block',
    textAlign: 'left',
    color: lighten(theme.palette.text.primary, 0.3)
  }
}));

export const divider = <Divider variant="middle" component="li" />;

export const listItem = (label, value, icon = null, customTestId = null) => {
  const testId = sanitizeTestId(customTestId || label);
  return (
    <ListItem>
      <ListItemText>
        <Box display="flex">
          {icon && <Box ml={1}>{icon}</Box>}
          <Box flexGrow={1}>{label}</Box>
          <TestDataAttribute id={testId}>
            <Box>{value}</Box>
          </TestDataAttribute>
        </Box>
      </ListItemText>
    </ListItem>
  );
};

enum LeadDetailsTab {
  CONTACT_DETAILS = 'CONTACT_DETAILS',
  REFERRAL_DETAILS = 'REFERRAL_DETAILS',
}

const TABS = [
  {
    value: LeadDetailsTab.CONTACT_DETAILS,
    label: 'Contact Details',
  },
  {
    value: LeadDetailsTab.REFERRAL_DETAILS,
    label: 'Referral Details',
  },
];

type LeadDetailsProps = {
  leadId: string;
  readOnly: boolean;
  salesforceLeadStore: any;
  onClose?: (() => void) | null;
  onSave?: (() => void) | null;
  noteDialogContext?: 'default' | 'inline';
};

export const LeadDetailsComponent = observer(
  ({
    leadId,
    readOnly,
    salesforceLeadStore,
    onClose = null,
    onSave = null,
    noteDialogContext = 'default',
  }: {
    leadId: string;
    readOnly: boolean;
    salesforceLeadStore: any;
    onClose?: (() => void) | null;
    onSave?: (() => void) | null;
    noteDialogContext?: 'default' | 'inline';
  }) => {
    const { globalStore } = salesforceLeadStore;
    const [tab, setTab] = useState(LeadDetailsTab.CONTACT_DETAILS);

    useEffect(() => {
      salesforceLeadStore.gridBusinessUnit = globalStore.lenderInfo?.businessUnit;

      return () => {
        salesforceLeadStore.resetLeadDetails();
      }
    }, []);

    return (
      <>
        <Box pb={2}>
          <Tabs
            selectedTab={TABS.findIndex(t => t.value === tab)}
            tabs={Object.values(TABS)}
            onTabChange={(e, i) => setTab(TABS[i].value)}
          />
        </Box>
        <Box display="flex" flexDirection="column" height="100%">
          <Box flex={1}>
            {tab === LeadDetailsTab.CONTACT_DETAILS &&
              <LeadDetails
                leadId={leadId}
                readOnly={readOnly}
                salesforceLeadStore={salesforceLeadStore}
                onClose={onClose}
                onSave={onSave}
                noteDialogContext={noteDialogContext}
              />}
            {tab === LeadDetailsTab.REFERRAL_DETAILS && <ReferrerCardComponent leadId={Number(leadId)} />}
          </Box>
        </Box>
      </>
    );
  }
);

const LeadDetails = observer(
  ({
    leadId,
    readOnly,
    salesforceLeadStore,
    onClose = null,
    onSave = null,
    noteDialogContext = 'default',
  }: LeadDetailsProps) => {
    const { leadsStore, leadDetails, globalStore } = salesforceLeadStore;
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
      if (salesforceLeadStore.gridBusinessUnit) {
        if (!leadDetails || leadId !== leadDetails.id) {
          fetchLeadData();
        }
      }
    }, [leadId, salesforceLeadStore.gridBusinessUnit]);

    useEffect(() => {
      if (leadDetails && !initialized) {
        if (leadDetails?.relatedLeads && leadDetails.relatedLeads.length && leadDetails.relatedLeads.findIndex(rl => rl === salesforceLeadStore.gridBusinessUnit) < 0) {
          salesforceLeadStore.gridBusinessUnit = leadDetails.relatedLeads[0];
        }
        setInitialized(true);
      }

    }, [leadDetails]);

    const fetchLeadData = async () => {
      await salesforceLeadStore.getLeadDetails(leadId);
    }

    if (!initialized) {
      return <></>;
    }

    return (<>
      {leadDetails?.relatedLeads.length > 0 &&
        <MuiTabs
          value={salesforceLeadStore.gridBusinessUnit}
          textColor='primary'
          indicatorColor='primary'
          onChange={(event, newValue) => { salesforceLeadStore.gridBusinessUnit = newValue }}
        >
          {leadDetails?.relatedLeads.map((relatedLead: string) => <Tab label={relatedLead} value={relatedLead} />)}
        </MuiTabs>}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ContactInformationCard
            leadId={leadId}
            readOnly={readOnly}
            salesforceLeadStore={salesforceLeadStore}
            onClose={onClose}
            onSave={onSave}
          />
          <Box pt={2}>
            <AdditionalContactsCard leadDetailsStore={leadsStore.leadDetailsStore} />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <ContactNotesCard
            leadDetailsStore={leadsStore.leadDetailsStore}
            recordId={leadId}
            recordType={LEAD_OBJECT_TYPE}
            dialogContext={noteDialogContext}
          />
          <Box pt={2}>
            <ContactActivitiesCard
              leadDetailsStore={leadsStore.leadDetailsStore}
            />
          </Box>
          <Box pt={2}>
            <RealEstateTransactions
              leadDetailsStore={leadsStore.leadDetailsStore}
            />
          </Box>
          <Box pt={2}>
            <ContactTasksCard
              leadDetailsStore={leadsStore.leadDetailsStore}
            />
          </Box>
          <Box pt={2}>
            <ContactHistoryCard
              leadDetailsStore={leadsStore.leadDetailsStore}
              type={LEAD_OBJECT_TYPE} />
          </Box>
        </Grid>
      </Grid>
    </>
    )
  });

const ContactInformationCard = observer(
  ({ leadId, readOnly, salesforceLeadStore, onClose, onSave }) => {
    const classes = useStyles();
    const formStore = salesforceLeadStore.editLeadDetailsFormStore;
    const {
      userStore: { userInformation },
    } = useUserStore();
    const [openSendBulkMessagesModal, setOpenSendBulkMessagesModal] = useState(
      false
    );
    const [openSendBulkEmailModal, setOpenSendBulkEmailModal] = useState(false);
    const { ringCentralStore, sendEmailsStore } = salesforceLeadStore;
    const { leadsStore, globalStore, leadDetails } = salesforceLeadStore;
    const { leadDetailsStore } = leadsStore;
    const [subStatusOptions, setSubStatusOptions] = useState([]);
    const [leadDetailsRequest, setLeadDetailsRequest] = useState(true);
    const [dncLoaded, setDncLoaded] = useState(false);
    const { userDetailStore } = useNewLeadStore().newLeadStore;
    const connectedWithRingCentral = salesforceLeadStore.isSalesForce ? salesforceLeadStore.connectedWithRingcentral : userInformation.connectedWithRingcentral;
    const [openModal, setOpenModal] = useState(false);

    useEffect(() => {
      userDetailStore.getLeadSourceOptions();
    }, []);

    useEffect(() => {
      if (salesforceLeadStore.gridBusinessUnit) {
        leadDetailsStore.fetchLeadDetails(leadId, LEAD_OBJECT_TYPE, salesforceLeadStore.gridBusinessUnit);
        fetchData();
      }
      salesforceLeadStore.currentRole = salesforceLeadStore.currentRole || userInformation?.roles[0] || '';
      salesforceLeadStore.emailAddress = salesforceLeadStore.emailAddress || userInformation?.username || '';
    }, [leadId, salesforceLeadStore.gridBusinessUnit]);

    useEffect(() => {

      if (dncLoaded) return;

      const details = salesforceLeadStore.leadDetails;
      if (!details) return;

      let primaryPhone = formStore?.form?.fields?.phoneNumber?.value;
      let secondaryPhone = formStore?.form?.fields?.secondaryPhone?.value;

      if (!primaryPhone) {
        primaryPhone = details.phoneNumber;
      }
      if (!secondaryPhone) {
        secondaryPhone = details.secondaryPhone;
      }

      if (primaryPhone || secondaryPhone) {
        leadDetailsStore.fetchLeaDncDetails(
          Number(leadId),
          LEAD_OBJECT_TYPE,
          primaryPhone,
          secondaryPhone
        );
        setDncLoaded(true);
      }
    }, [
      salesforceLeadStore.leadDetails,
      dncLoaded,
      formStore,
      leadDetailsStore,
      leadId,
    ]);

    useEffect(() => {
      if (leadDetailsRequest && salesforceLeadStore.leadDetails && globalStore.userFeatures.backOfficeLead) {
        salesforceLeadStore.gridBusinessUnit = salesforceLeadStore?.leadDetails?.relatedLeads[0];
        setLeadDetailsRequest(false);
      }
    }, [salesforceLeadStore.leadDetails]);

    useEffect(() => {
      updateSubStatusOptions(formStore.form.fields.status.value);
    }, [formStore.form.fields.status.value]);

    const fetchData = async () => {
      await salesforceLeadStore.getAllOwners();
      await salesforceLeadStore.getLeadDetails(leadId);
      await salesforceLeadStore.getAdditionalContactInfo(leadId);
    };

    const onSendBulkMessages = () => {
      ringCentralStore.resetLists();
      ringCentralStore.addStandaloneRecord(leadId, { phone: formStore.form.fields.phoneNumber.value, secondaryPhoneNumber: formStore.form.fields.secondaryPhone.value }, LEAD_OBJECT_TYPE);
      setOpenSendBulkMessagesModal(true);
    };

    const onSendBulkEmail = () => {
      sendEmailsStore.resetbulkEmails();
      sendEmailsStore.setBulkEmails(
        {
          email: formStore.form.fields.email.value,
          firstName: formStore.form.fields.firstName.value,
          lastName: formStore.form.fields.lastName.value,
          id: leadId,
          type: LEAD_OBJECT_TYPE
        }, true);
      setOpenSendBulkEmailModal(true);
    };

    const closeModal = () => {
      setOpenSendBulkMessagesModal(false);
      setOpenSendBulkEmailModal(false);
      ringCentralStore.setMessage('');
      ringCentralStore.setLabel('');
      ringCentralStore.setTemplateValue('');
      ringCentralStore.setTemplateId('');
      ringCentralStore.setTemplateLabel('');
      ringCentralStore.sendBulkSMSFormStore.onFieldChange('message', '');
      ringCentralStore.resetLists();
    };

    const closeBulkEmailModal = () => {
      setOpenSendBulkEmailModal(false);
      sendEmailsStore.handleModalClose();
    };

    const updateSubStatusOptions = selectedStatus => {
      switch (selectedStatus) {
        case 'New':
          setSubStatusOptions(leadNewSubStatusOptions);
          break;
        case 'Attempting to contact':
          setSubStatusOptions(leadAttemptingToContactSubStatusOptions);
          break;
        case 'Fit':
          setSubStatusOptions(leadFitSubStatusOptions);
          break;
        case 'No Fit':
          setSubStatusOptions(leadNoFitSubStatusOptions);
          break;
        default:
          setSubStatusOptions([]);
          break;
      }
    };

    const handleStatusChange = selectedStatus => {
      formStore.onFieldChange('status', selectedStatus);
      updateSubStatusOptions(selectedStatus);
    };

    const handleOpenModal = () => {
      setOpenModal(true);
    };

    const handleCloseModal = () => {
      setOpenModal(false);
    };

    const convertLeadToLender = async () => {
      await salesforceLeadStore.convertLeadToLender(leadDetailsObject);
      handleCloseModal();
    };

    const handleAddressChange = event => {
      const { value } = event?.target || {};
      formStore.onFieldChange('propertyAddress', value);
    };

    const leadDetailsObject = {
      leadId: salesforceLeadStore.leadDetails?.id || '',
      company: salesforceLeadStore.leadDetails?.company || '',
      recordType: salesforceLeadStore.leadDetails?.recordType || '',
      fromLeadDetails: true,
    };

    const sortedLeadSourceList = useMemo(() => {
      const sourceList = userDetailStore?.leadSourceOptions ? userDetailStore.leadSourceOptions : [];

      if(sourceList.length === 0) return [];

      return [...sourceList].sort((a, b) => a.label.localeCompare(b.label))

    }, [userDetailStore?.leadSourceOptions])

    const action = (
      <Box p={1} mb={-10}>
        <Button
          className={classes.button}
          variant="contained"
          size="small"
          color="primary"
          testId="convert_lead"
          onClick={handleOpenModal}
        >
          Convert
        </Button>
        <Button
          className={classes.button}
          variant="contained"
          size="small"
          onClick={() =>
            (window.location.href = `rcapp://r/call?number=${formStore.form.fields.phoneNumber.value}`)
          }
          color="primary"
          testId="call"
          disabled={formStore.form.fields?.doNotCall.value || formStore.form.fields?.brokered.value}
        >
          Call
        </Button>
        <Button
          className={classes.button}
          variant="contained"
          size="small"
          onClick={onSendBulkMessages}
          color="primary"
          testId="send_bulk_messages"
          disabled={formStore.form.fields?.doNotSms.value || formStore.form.fields?.brokered.value}
        >
          SMS
        </Button>
        <Button
          className={classes.button}
          variant="contained"
          size="small"
          onClick={onSendBulkEmail}
          color="primary"
          testId="send_bulk_emails"
          disabled={formStore.form.fields?.doNotSendEmail.value || formStore.form.fields?.brokered.value}
        >
          Email
        </Button>
      </Box>
    );

    return (
      <Card title={'Contact Information'} cardHeaderProps={{ action }}>
        <Box pb={3}>
          <SalesUserTag
            store={salesforceLeadStore}
            formStore={formStore}
            details={leadDetails}
            isContactTag={false}
          />
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'First Name',
              fieldName: 'firstName',
              testId: 'firstName',
              store: formStore,
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'Last Name',
              fieldName: 'lastName',
              testId: 'lastName',
              store: formStore,
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createPhoneFieldClick({
              label: 'Phone Number',
              fieldName: 'phoneNumber',
              testId: 'phoneNumber',
              store: formStore,
              disabled: readOnly,
              InputProps: {
                endAdornment: (
                  <InputAdornment position="end">
                    {leadDetailsStore.dncPrimary?.lineType ? (
                      <Tooltip
                        title={`Line type: ${leadDetailsStore.dncPrimary.lineType}`}
                      >
                        {leadDetailsStore.dncPrimary.lineType === 'AllOther' ? (
                          <PhoneIcon color="primary" />
                        ) : (
                          <SmartphoneIcon color="primary" />
                        )}
                      </Tooltip>
                    ) : (
                      <Tooltip title="No line type info">
                        <HelpOutline fontSize="medium" color="primary" />
                      </Tooltip>
                    )}
                  </InputAdornment>
                ),
              },
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createPhoneFieldClick({
              label: 'Secondary Phone Number',
              fieldName: 'secondaryPhone',
              testId: 'secondaryPhone',
              store: formStore,
              disabled: readOnly,
              InputProps: {
                endAdornment: (
                  <InputAdornment position="end">
                    {leadDetailsStore.dncSecondary?.lineType ? (
                      <Tooltip
                        title={`Line type: ${leadDetailsStore.dncSecondary.lineType}`}
                      >
                        {leadDetailsStore.dncSecondary.lineType === 'AllOther' ? (
                          <PhoneIcon color="primary" />
                        ) : (
                          <SmartphoneIcon color="primary" />
                        )}
                      </Tooltip>
                    ) : (
                      <Tooltip title="No line type info">
                        <HelpOutline fontSize="medium" color="primary" />
                      </Tooltip>
                    )}
                  </InputAdornment>
                ),
              },
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'Email',
              fieldName: 'email',
              testId: 'email',
              type: 'email',
              store: formStore,
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'Secondary Email',
              fieldName: 'secondaryEmail',
              testId: 'secondaryEmail',
              type: 'email',
              store: formStore,
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'Company',
              fieldName: 'company',
              testId: 'company',
              store: formStore,
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {userDetailStore?.loadingDropdownOptions && createAutocompleteField({
              label: 'Lead Source',
              fieldName: 'leadSource',
              store: formStore,
              options: sortedLeadSourceList,
              onChange: value => {
                formStore.onFieldChange('leadSource', value);
              },
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'Data Source',
              fieldName: 'dataSource',
              testId: 'dataSource',
              store: formStore,
              disabled: readOnly,
              InputProps: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={
                        <span style={{ fontSize: '16px' }}>
                          Please enter the name of the list here. e.g NPLA Conf
                          2024
                        </span>
                      }
                    >
                      <HelpOutline fontSize="medium" color={'primary'} />
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createSelectField({
              label: 'Data Source Group',
              fieldName: 'dataSourceGroup',
              testId: 'dataSourceGroup',
              store: formStore,
              options: dataSourceGroupOptions,
              disabled: readOnly,
              onChange: value => {
                formStore.onFieldChange('dataSourceGroup', value);
              },
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createSelectField({
              label: 'Status',
              fieldName: 'status',
              testId: 'status',
              store: formStore,
              options: statusOptions,
              placeholder: 'Choose Status',
              disabled: readOnly,
              onChange: value => {
                formStore.onFieldChange('status', value);
                handleStatusChange(value);
              },
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createSelectField({
              label: 'Sub Status',
              fieldName: 'subStatus',
              testId: 'subStatus',
              store: formStore,
              options: subStatusOptions,
              placeholder: 'Choose Sub Status',
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createSelectField({
              label: 'Type',
              fieldName: 'type',
              testId: 'type',
              store: formStore,
              options: leadTypeOptions,
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createSelectField({
              label: 'Profession',
              fieldName: 'profession',
              testId: 'profession',
              store: formStore,
              options: leadProfessionOptions,
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createSelectField({
              label: 'Record Type',
              fieldName: 'recordType',
              testId: 'recordType',
              store: formStore,
              options: recordTypeLeadOptions,
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createSelectField({
              label: 'Experience',
              fieldName: 'experience',
              testId: 'experience',
              store: formStore,
              options: leadExperienceOptions,
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'How Did you Hear About Us?',
              fieldName: 'howDidYouHearAboutUs',
              testId: 'howDidYouHearAboutUs',
              store: formStore,
              disabled: readOnly,
            })}
          </Grid>
          {(salesforceLeadStore?.currentRole === 'backOfficeProcessor' ||
            salesforceLeadStore?.currentRole === 'backOfficeLead' ||
            salesforceLeadStore?.currentRole === 'backOfficeOriginator' ||
            salesforceLeadStore?.currentRole === 'backOfficeTeamLead') &&
            <Grid item xs={12} sm={6}>
              {createAutocompleteField({
                label: 'Owner Name',
                fieldName: 'ownerId',
                store: formStore,
                options: salesforceLeadStore.getActiveOwnersByBusinessUnit(),
                placeholder: 'Choose owner',
                disabled: readOnly,
                onChange: value => {
                  formStore.onFieldChange('ownerId', value);
                },
              })}
            </Grid>}
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'Promo Code',
              fieldName: 'promoCode',
              testId: 'promoCode',
              store: formStore,
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'Lead Feedback',
              fieldName: 'leadFeedback',
              testId: 'leadFeedback',
              store: formStore,
              disabled: readOnly,
              multiline: true
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'Linkedin URL',
              fieldName: 'linkedinUrl',
              testId: 'linkedinUrl',
              store: formStore,
              disabled: readOnly,
              multiline: true
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'Facebook URL',
              fieldName: 'facebookUrl',
              testId: 'facebookUrl',
              store: formStore,
              disabled: readOnly,
              multiline: true
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'Instagram URL',
              fieldName: 'instagramUrl',
              testId: 'instagramUrl',
              store: formStore,
              disabled: readOnly,
              multiline: true
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createTextField({
              label: 'What can we help you with?',
              fieldName: 'helpComments',
              testId: 'helpComments',
              store: formStore,
              disabled: readOnly,
              multiline: true
            })}
          </Grid>
          <Grid item xs={12}>
            {createAutocompleteAddressField({
              store: formStore,
              testId: 'address',
              fieldName: 'address',
              label: 'Address',
              onChange: handleAddressChange,
            })}
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h4" style={{ fontSize: 20 }}>
              Do Not Disturb
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            {createCheckboxField({
              label: 'Do not Call',
              fieldName: 'doNotCall',
              store: formStore,
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createCheckboxField({
              label: 'Do not Email',
              fieldName: 'doNotSendEmail',
              store: formStore,
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createCheckboxField({
              label: 'Do not SMS',
              fieldName: 'doNotSms',
              store: formStore,
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createCheckboxField({
              label: 'Do not Mail',
              fieldName: 'doNotMail',
              store: formStore,
              disabled: readOnly,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {createCheckboxField({
              label: 'Brokered',
              fieldName: 'brokered',
              store: formStore,
              disabled: readOnly,
            })}
          </Grid>
          <StandardDialog
            open={openSendBulkMessagesModal}
            title={connectedWithRingCentral ? ' ' : 'Send SMS'}
            maxWidth="lg"
            handleClose={closeModal}
            dialogContent={
              <SendBulkMessagesModal
                handleClose={closeModal}
                disableEditTemplateBody={false}
                salesforceLeadStore={salesforceLeadStore}
                type={LEAD_OBJECT_TYPE}
              />
            }
          />
          <StandardDialog
            open={openSendBulkEmailModal}
            title={'Send Bulk Emails'}
            maxWidth="xl"
            handleClose={closeBulkEmailModal}
            dialogContent={
              <SendBulkEmailsModal
                handleClose={closeBulkEmailModal}
                disableEditTemplateBody={false}
                salesforceLeadStore={salesforceLeadStore}
                type={LEAD_OBJECT_TYPE}
              />
            }
          />
          <Dialog open={openModal} onClose={handleCloseModal}>
            <DialogTitle>Convert Lead to Lender</DialogTitle>
            <DialogContent>
              <ConvertLeadToLenderModal record={leadDetailsObject} />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseModal}
                color="primary"
                testId="cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={convertLeadToLender}
                color="primary"
                testId="cancel"
              >
                Submit
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
        {onClose && onSave && (
          <Box pt={4} display="flex" justifyContent="flex-end">
            <Button
              style={{ marginRight: '8px' }}
              onClick={onClose}
              color="primary"
              variant="outlined"
              testId="cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={onSave}
              color="primary"
              variant="contained"
              testId="save"
            >
              Apply Changes
            </Button>
          </Box>
        )}
        <Box pt={2}>
          <Typography variant="subtitle2" className={classes.noteTime}>
            {leadDetails?.brandDataCreatedDate && <>Created by {leadDetails?.brandDataCreatedBy} at {formatDate(leadDetails?.brandDataCreatedDate, null)}<br /></>}
            {leadDetails?.brandDataLastUpdatedDate && <>Last updated by {leadDetails?.brandDataLastUpdatedBy} at {formatDate(leadDetails?.brandDataLastUpdatedDate, null)}</>}
          </Typography>
        </Box>
        <StandardDialog
          open={salesforceLeadStore.showRemoveFromJourneysConfirmation}
          title="Remove from current journeys"
          maxWidth="sm"
          handleClose={() => salesforceLeadStore.setShowRemoveFromJourneysConfirmation(false)}
          dialogContent={
            <Typography variant="body1">
              This lead is currently active in a cadence. Do you want to remove it from its current journeys and assign the new owner?
            </Typography>
          }
          dialogActions={
            <>
              <Button
                style={{ marginRight: '8px' }}
                onClick={() => salesforceLeadStore.setShowRemoveFromJourneysConfirmation(false)}
                color="primary"
                variant="outlined"
                testId="cancelRemoveFromCurrentJourneys"
              >
                Cancel
              </Button>
              <Button
                onClick={() => salesforceLeadStore.editLeadDetails(leadId, true)}
                color="primary"
                variant="contained"
                testId="confirmRemoveFromCurrentJourneys"
              >
                Remove and Save
              </Button>
            </>
          }
        />
      </Card>
    );
  }
);
